import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

import {
  normalizeGeneratedDocumentSource,
  normalizeGeneratedDocumentStatus,
  type GeneratedDocumentSource,
  type GeneratedDocumentStatus,
} from '@/lib/generatedDocumentPresentation';

import {
  buildVariableDefinitions,
  type DocumentVariableDefinition,
} from '@/lib/documentTemplateVariables';

export interface GeneratedDocument {
  id: string;

  templateId:
    string | null;

  templateName:
    string | null;

  templateVersionNumber:
    number | null;

  templateVersionVariables:
    string[];

  templateVersionVariablesSchema:
    DocumentVariableDefinition[];

  clientId:
    string | null;

  clientName:
    string | null;

  caseId:
    string | null;

  caseTitle:
    string | null;

  leadId:
    string | null;

  leadName:
    string | null;

  legalProductId:
    string | null;

  legalProductName:
    string | null;

  title: string;

  documentType:
    string;

  status:
    GeneratedDocumentStatus;

  source:
    GeneratedDocumentSource;

  content:
    string;

  renderedContent:
    string | null;

  variablesSnapshot:
    Record<string, string>;

  notes:
    string | null;

  currentVersion:
    number;

  isActive:
    boolean;

  finalizedAt:
    string | null;

  finalizedByUserId:
    string | null;

  createdAt:
    string;

  updatedAt:
    string;
}

export interface GeneratedDocumentVersion {
  id: string;

  versionNumber:
    number;

  templateId:
    string | null;

  templateVersionNumber:
    number | null;

  clientId:
    string | null;

  caseId:
    string | null;

  leadId:
    string | null;

  legalProductId:
    string | null;

  title: string;

  documentType:
    string;

  status:
    GeneratedDocumentStatus;

  source:
    GeneratedDocumentSource;

  content:
    string;

  renderedContent:
    string | null;

  variablesSnapshot:
    Record<string, string>;

  notes:
    string | null;

  isActive:
    boolean;

  finalizedAt:
    string | null;

  finalizedByUserId:
    string | null;

  changeNote:
    string | null;

  createdByUserId:
    string | null;

  createdAt:
    string;
}

export interface CreateGeneratedDocumentInput {
  templateId?:
    string | null;

  clientId?:
    string | null;

  caseId?:
    string | null;

  leadId?:
    string | null;

  legalProductId?:
    string | null;

  title: string;

  documentType:
    string;

  status?:
    string;

  source?:
    GeneratedDocumentSource;

  content:
    string;

  renderedContent?:
    string | null;

  variablesSnapshot?:
    Record<string, string>;

  notes?:
    string | null;

  isActive?:
    boolean;

  changeNote?:
    string | null;
}

export type UpdateGeneratedDocumentInput =
  Partial<
    CreateGeneratedDocumentInput
  >;

type UseGeneratedDocumentsOptions = {
  autoLoad?: boolean;
};

export type LoadGeneratedDocumentsFilters = {
  includeArchived?: boolean;

  status?: string;

  source?: string;

  templateId?: string;

  clientId?: string;

  caseId?: string;

  leadId?: string;

  legalProductId?: string;
};

const toNullableText = (
  value: unknown
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return null;
  }

  const text =
    String(value).trim();

  return text === ''
    ? null
    : text;
};

const toBoolean = (
  value: unknown,
  defaultValue = true
) => {
  if (
    value === undefined ||
    value === null ||
    value === ''
  ) {
    return defaultValue;
  }

  if (
    typeof value ===
    'boolean'
  ) {
    return value;
  }

  if (
    typeof value ===
    'number'
  ) {
    return value === 1;
  }

  const normalized =
    String(value)
      .trim()
      .toLowerCase();

  if (
    [
      '0',
      'false',
      'inactive',
      'inativo',
      'archived',
      'arquivado',
      'nao',
      'não',
    ].includes(normalized)
  ) {
    return false;
  }

  if (
    [
      '1',
      'true',
      'active',
      'ativo',
      'sim',
      's',
    ].includes(normalized)
  ) {
    return true;
  }

  return Boolean(value);
};

const toPositiveInteger = (
  value: unknown,
  fallback = 1
) => {
  const parsed =
    Number(value);

  return (
    Number.isFinite(parsed) &&
    parsed >= 1
  )
    ? Math.trunc(parsed)
    : fallback;
};

const normalizeVariablesSnapshot =
  (
    value: unknown
  ): Record<string, string> => {
    if (
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return {};
    }

    if (
      typeof value ===
      'string'
    ) {
      try {
        const parsed =
          JSON.parse(value);

        if (
          parsed &&
          typeof parsed ===
            'object' &&
          !Array.isArray(parsed)
        ) {
          return normalizeVariablesSnapshot(
            parsed
          );
        }
      } catch {
        return {};
      }
    }

    if (
      typeof value ===
        'object' &&
      !Array.isArray(value)
    ) {
      return Object.entries(
        value as
          Record<
            string,
            unknown
          >
      ).reduce<
        Record<string, string>
      >(
        (
          acc,
          [
            key,
            rawValue,
          ]
        ) => {
          acc[key] =
            toNullableText(
              rawValue
            ) ?? '';

          return acc;
        },
        {}
      );
    }

    return {};
  };

const normalizeJsonArray =
  (
    value: unknown
  ): unknown[] => {
    if (
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return [];
    }

    if (
      Array.isArray(value)
    ) {
      return value;
    }

    if (
      typeof value ===
      'string'
    ) {
      try {
        const parsed =
          JSON.parse(value);

        return Array.isArray(
          parsed
        )
          ? parsed
          : [];
      } catch {
        return [];
      }
    }

    return [];
  };

const normalizeStringArray =
  (
    value: unknown
  ): string[] =>
    normalizeJsonArray(value)
      .map(
        (item) =>
          String(
            item ?? ''
          ).trim()
      )
      .filter(Boolean);

const normalizeGeneratedDocument =
  (
    value: any
  ): GeneratedDocument => {
    const templateId =
      toNullableText(
        value?.templateId ??
          value?.template_id
      );

    const templateVersionVariables =
      normalizeStringArray(
        value?.templateVersionVariables ??
          value?.template_version_variables
      );

    const rawTemplateVersionVariablesSchema =
      normalizeJsonArray(
        value?.templateVersionVariablesSchema ??
          value?.template_version_variables_schema
      ) as DocumentVariableDefinition[];

    const templateVersionVariableKeys =
      templateVersionVariables.length > 0
        ? templateVersionVariables
        : rawTemplateVersionVariablesSchema.map(
            (definition) =>
              definition.key
          );

    const templateVersionRequiredKeys =
      rawTemplateVersionVariablesSchema
        .filter(
          (definition) =>
            definition.required
        )
        .map(
          (definition) =>
            definition.key
        );

    const templateVersionVariablesSchema =
      buildVariableDefinitions(
        templateVersionVariableKeys,
        rawTemplateVersionVariablesSchema,
        templateVersionRequiredKeys
      );

    return {
      id:
        String(
          value?.id ?? ''
        ),

      templateId,

      templateName:
        toNullableText(
          value?.templateName ??
            value?.template_name
        ),

      templateVersionNumber:
        value?.templateVersionNumber ===
          null ||
        value?.template_version_number ===
          null
          ? null
          : (
              value?.templateVersionNumber ??
              value?.template_version_number
            ) === undefined
            ? null
            : toPositiveInteger(
                value?.templateVersionNumber ??
                  value?.template_version_number
              ),

      templateVersionVariables,

      templateVersionVariablesSchema,

      clientId:
        toNullableText(
          value?.clientId ??
            value?.client_id
        ),

      clientName:
        toNullableText(
          value?.clientName ??
            value?.client_name
        ),

      caseId:
        toNullableText(
          value?.caseId ??
            value?.case_id
        ),

      caseTitle:
        toNullableText(
          value?.caseTitle ??
            value?.case_title
        ),

      leadId:
        toNullableText(
          value?.leadId ??
            value?.lead_id
        ),

      leadName:
        toNullableText(
          value?.leadName ??
            value?.lead_name
        ),

      legalProductId:
        toNullableText(
          value?.legalProductId ??
            value?.legal_product_id
        ),

      legalProductName:
        toNullableText(
          value?.legalProductName ??
            value?.legal_product_name
        ),

      title:
        String(
          value?.title ?? ''
        ).trim(),

      documentType:
        String(
          value?.documentType ??
            value?.document_type ??
            ''
        ).trim(),

      status:
        normalizeGeneratedDocumentStatus(
          value?.status
        ),

      source:
        normalizeGeneratedDocumentSource(
          value?.source,
          Boolean(templateId)
        ),

      content:
        String(
          value?.content ?? ''
        ),

      renderedContent:
        toNullableText(
          value?.renderedContent ??
            value?.rendered_content
        ),

      variablesSnapshot:
        normalizeVariablesSnapshot(
          value?.variablesSnapshot ??
            value?.variables_snapshot
        ),

      notes:
        toNullableText(
          value?.notes
        ),

      currentVersion:
        toPositiveInteger(
          value?.currentVersion ??
            value?.current_version
        ),

      isActive:
        toBoolean(
          value?.isActive ??
            value?.is_active,
          true
        ),

      finalizedAt:
        toNullableText(
          value?.finalizedAt ??
            value?.finalized_at
        ),

      finalizedByUserId:
        toNullableText(
          value?.finalizedByUserId ??
            value?.finalized_by_user_id
        ),

      createdAt:
        toNullableText(
          value?.createdAt ??
            value?.created_at
        ) ??
        new Date()
          .toISOString(),

      updatedAt:
        toNullableText(
          value?.updatedAt ??
            value?.updated_at
        ) ??
        new Date()
          .toISOString(),
    };
  };

const normalizeGeneratedDocumentVersion =
  (
    value: any
  ): GeneratedDocumentVersion => {
    const templateId =
      toNullableText(
        value?.templateId ??
          value?.template_id
      );

    return {
      id:
        String(
          value?.id ?? ''
        ),

      versionNumber:
        toPositiveInteger(
          value?.versionNumber ??
            value?.version_number
        ),

      templateId,

      templateVersionNumber:
        (
          value?.templateVersionNumber ??
          value?.template_version_number
        ) === undefined ||
        (
          value?.templateVersionNumber ??
          value?.template_version_number
        ) === null
          ? null
          : toPositiveInteger(
              value?.templateVersionNumber ??
                value?.template_version_number
            ),

      clientId:
        toNullableText(
          value?.clientId ??
            value?.client_id
        ),

      caseId:
        toNullableText(
          value?.caseId ??
            value?.case_id
        ),

      leadId:
        toNullableText(
          value?.leadId ??
            value?.lead_id
        ),

      legalProductId:
        toNullableText(
          value?.legalProductId ??
            value?.legal_product_id
        ),

      title:
        String(
          value?.title ?? ''
        ).trim(),

      documentType:
        String(
          value?.documentType ??
            value?.document_type ??
            ''
        ).trim(),

      status:
        normalizeGeneratedDocumentStatus(
          value?.status
        ),

      source:
        normalizeGeneratedDocumentSource(
          value?.source,
          Boolean(templateId)
        ),

      content:
        String(
          value?.content ?? ''
        ),

      renderedContent:
        toNullableText(
          value?.renderedContent ??
            value?.rendered_content
        ),

      variablesSnapshot:
        normalizeVariablesSnapshot(
          value?.variablesSnapshot ??
            value?.variables_snapshot
        ),

      notes:
        toNullableText(
          value?.notes
        ),

      isActive:
        toBoolean(
          value?.isActive ??
            value?.is_active,
          true
        ),

      finalizedAt:
        toNullableText(
          value?.finalizedAt ??
            value?.finalized_at
        ),

      finalizedByUserId:
        toNullableText(
          value?.finalizedByUserId ??
            value?.finalized_by_user_id
        ),

      changeNote:
        toNullableText(
          value?.changeNote ??
            value?.change_note
        ),

      createdByUserId:
        toNullableText(
          value?.createdByUserId ??
            value?.created_by_user_id
        ),

      createdAt:
        toNullableText(
          value?.createdAt ??
            value?.created_at
        ) ??
        new Date()
          .toISOString(),
    };
  };

const sortGeneratedDocuments =
  (
    a: GeneratedDocument,
    b: GeneratedDocument
  ) =>
    b.updatedAt.localeCompare(
      a.updatedAt
    ) ||
    b.createdAt.localeCompare(
      a.createdAt
    ) ||
    a.title.localeCompare(
      b.title,
      'pt-BR'
    );

export function useGeneratedDocuments(
  options:
    UseGeneratedDocumentsOptions = {}
) {
  const {
    autoLoad = true,
  } = options;

  const [
    generatedDocuments,
    setGeneratedDocuments,
  ] =
    useState<
      GeneratedDocument[]
    >([]);

  const [
    isLoadingGeneratedDocuments,
    setIsLoadingGeneratedDocuments,
  ] =
    useState(true);

  const [
    generatedDocumentsError,
    setGeneratedDocumentsError,
  ] =
    useState<
      string | null
    >(null);

  const requestIdRef =
    useRef(0);

  const loadGeneratedDocuments =
    useCallback(
      async (
        filters:
          LoadGeneratedDocumentsFilters = {}
      ) => {
        const requestId =
          ++requestIdRef.current;

        setIsLoadingGeneratedDocuments(
          true
        );

        setGeneratedDocumentsError(
          null
        );

        try {
          const searchParams =
            new URLSearchParams();

          if (
            filters.includeArchived
          ) {
            searchParams.set(
              'includeArchived',
              '1'
            );
          }

          if (
            filters.status?.trim()
          ) {
            searchParams.set(
              'status',
              filters.status.trim()
            );
          }

          if (
            filters.source?.trim()
          ) {
            searchParams.set(
              'source',
              filters.source.trim()
            );
          }

          if (
            filters.templateId?.trim()
          ) {
            searchParams.set(
              'templateId',
              filters.templateId.trim()
            );
          }

          if (
            filters.clientId?.trim()
          ) {
            searchParams.set(
              'clientId',
              filters.clientId.trim()
            );
          }

          if (
            filters.caseId?.trim()
          ) {
            searchParams.set(
              'caseId',
              filters.caseId.trim()
            );
          }

          if (
            filters.leadId?.trim()
          ) {
            searchParams.set(
              'leadId',
              filters.leadId.trim()
            );
          }

          if (
            filters.legalProductId?.trim()
          ) {
            searchParams.set(
              'legalProductId',
              filters.legalProductId.trim()
            );
          }

          const endpoint =
            searchParams.toString()
              ? `/api/generated-documents?${searchParams.toString()}`
              : '/api/generated-documents';

          const response =
            await fetchWithRetry(
              endpoint
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.generatedDocuments
            )
          ) {
            throw new Error(
              data.error ||
                `GET /api/generated-documents falhou com status ${response.status}`
            );
          }

          if (
            requestId ===
            requestIdRef.current
          ) {
            setGeneratedDocuments(
              data.generatedDocuments
                .map(
                  normalizeGeneratedDocument
                )
                .sort(
                  sortGeneratedDocuments
                )
            );
          }
        } catch (error) {
          console.error(
            'Erro ao carregar documentos gerados reais.',
            error
          );

          if (
            requestId ===
            requestIdRef.current
          ) {
            setGeneratedDocumentsError(
              'Não foi possível carregar os documentos reais. Mantendo os dados atuais.'
            );
          }
        } finally {
          if (
            requestId ===
            requestIdRef.current
          ) {
            setIsLoadingGeneratedDocuments(
              false
            );
          }
        }
      },
      []
    );

  const loadGeneratedDocumentHistory =
    useCallback(
      async (
        id: string
      ) => {
        const response =
          await fetchWithRetry(
            `/api/generated-documents/${encodeURIComponent(
              id
            )}?includeVersions=1`
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.generatedDocument ||
          !Array.isArray(
            data.versions
          )
        ) {
          throw new Error(
            data.error ||
              `GET histórico do documento ${id} falhou com status ${response.status}`
          );
        }

        return {
          generatedDocument:
            normalizeGeneratedDocument(
              data.generatedDocument
            ),

          versions:
            data.versions
              .map(
                normalizeGeneratedDocumentVersion
              )
              .sort(
                (
                  a:
                    GeneratedDocumentVersion,
                  b:
                    GeneratedDocumentVersion
                ) =>
                  b.versionNumber -
                  a.versionNumber
              ),
        };
      },
      []
    );

  const createGeneratedDocument =
    useCallback(
      async (
        input:
          CreateGeneratedDocumentInput
      ) => {
        const response =
          await fetchWithRetry(
            '/api/generated-documents',
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.generatedDocument
        ) {
          throw new Error(
            data.error ||
              `POST /api/generated-documents falhou com status ${response.status}`
          );
        }

        const created =
          normalizeGeneratedDocument(
            data.generatedDocument
          );

        setGeneratedDocuments(
          (prev) =>
            [
              created,
              ...prev.filter(
                (item) =>
                  item.id !==
                  created.id
              ),
            ].sort(
              sortGeneratedDocuments
            )
        );

        return created;
      },
      []
    );

  const updateGeneratedDocument =
    useCallback(
      async (
        id: string,
        input:
          UpdateGeneratedDocumentInput
      ) => {
        const response =
          await fetchWithRetry(
            `/api/generated-documents/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'PUT',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.generatedDocument
        ) {
          throw new Error(
            data.error ||
              `PUT /api/generated-documents/${id} falhou com status ${response.status}`
          );
        }

        const updated =
          normalizeGeneratedDocument(
            data.generatedDocument
          );

        setGeneratedDocuments(
          (prev) =>
            [
              updated,
              ...prev.filter(
                (item) =>
                  item.id !==
                  updated.id
              ),
            ].sort(
              sortGeneratedDocuments
            )
        );

        return updated;
      },
      []
    );

  const deleteGeneratedDocument =
    useCallback(
      async (
        id: string
      ) => {
        const response =
          await fetchWithRetry(
            `/api/generated-documents/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'DELETE',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              `DELETE /api/generated-documents/${id} falhou com status ${response.status}`
          );
        }

        setGeneratedDocuments(
          (prev) =>
            prev.filter(
              (item) =>
                item.id !== id
            )
        );
      },
      []
    );

  useEffect(
    () => {
      if (!autoLoad) {
        return;
      }

      void loadGeneratedDocuments();
    },
    [
      autoLoad,
      loadGeneratedDocuments,
    ]
  );

  return {
    generatedDocuments,

    isLoadingGeneratedDocuments,

    generatedDocumentsError,

    loadGeneratedDocuments,

    loadGeneratedDocumentHistory,

    createGeneratedDocument,

    updateGeneratedDocument,

    deleteGeneratedDocument,
  };
}